/**
 * SandboxFileSystem — implements file operations via REST calls to the SandboxController.
 */

import { ENCODED_JSON_CONTENT_TYPE, encodeJsonBody } from '~/lib/encoded-json';

export interface FileWatchEvent {
  type: string;
  path: string;
  content?: string | null;
  buffer?: Uint8Array;
}

export interface WatchOptions {
  include?: string[];
  exclude?: string[];
  includeContent?: boolean;
}

export class SandboxFileSystem {
  private _getChatId: () => string | null;
  private _watchCallbacks: Array<(events: FileWatchEvent[]) => void> = [];

  constructor(getChatId: () => string | null) {
    this._getChatId = getChatId;
  }

  private get _chatId(): string {
    const id = this._getChatId();

    if (!id) {
      throw new Error('Sandbox not booted');
    }

    return id;
  }

  async readFile(path: string, _encoding?: string): Promise<string> {
    const res = await fetch(`/api/sandbox/${this._chatId}/files?path=${encodeURIComponent(path)}`, {
      credentials: 'include',
    });

    if (!res.ok) {
      throw new Error(`Failed to read file: ${path}`);
    }

    const data = await res.json();

    return data.content;
  }

  async writeFile(path: string, content: string | Uint8Array): Promise<void> {
    const textContent = typeof content === 'string' ? content : new TextDecoder().decode(content);
    // File contents are code — base64 them so the edge firewall can't 403 the write (see encoded-json).
    const res = await fetch(`/api/sandbox/${this._chatId}/files`, {
      method: 'POST',
      headers: { 'Content-Type': ENCODED_JSON_CONTENT_TYPE },
      credentials: 'include',
      body: encodeJsonBody({ path, content: textContent }),
    });

    if (!res.ok) {
      throw new Error(`Failed to write file: ${path}`);
    }
  }

  async mkdir(path: string, _options?: { recursive?: boolean }): Promise<void> {
    const res = await fetch(`/api/sandbox/${this._chatId}/files/mkdir`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ path }),
    });

    if (!res.ok) {
      throw new Error(`Failed to create directory: ${path}`);
    }
  }

  async readdir(path: string, options?: { withFileTypes?: boolean }): Promise<any[]> {
    const res = await fetch(`/api/sandbox/${this._chatId}/files/list?path=${encodeURIComponent(path)}`, {
      credentials: 'include',
    });

    if (!res.ok) {
      throw new Error(`Failed to list directory: ${path}`);
    }

    const data = await res.json();
    const entries: Array<{ path: string; type: 'file' | 'directory' }> = data.entries || [];

    if (!options?.withFileTypes) {
      return entries.map((e) => e.path.split('/').pop() || e.path);
    }

    // Return dirent-like objects with isFile() and isDirectory() methods
    return entries.map((entry) => ({
      name: entry.path.split('/').pop() || entry.path,
      isFile: () => entry.type === 'file',
      isDirectory: () => entry.type === 'directory',
    }));
  }

  async rm(path: string, _options?: { recursive?: boolean }): Promise<void> {
    const res = await fetch(`/api/sandbox/${this._chatId}/files?path=${encodeURIComponent(path)}`, {
      method: 'DELETE',
      credentials: 'include',
    });

    if (!res.ok) {
      throw new Error(`Failed to delete: ${path}`);
    }
  }

  /**
   * Watch for file changes. The SandboxGateway pushes events via WebSocket.
   * This method registers a callback that receives batched events.
   */
  watch(_options: WatchOptions, callback: (events: FileWatchEvent[]) => void): void {
    this._watchCallbacks.push(callback);
  }

  /**
   * Called by the SandboxRuntime when WebSocket file events arrive.
   */
  handleWatchEvent(event: FileWatchEvent): void {
    this.handleWatchEvents([event]);
  }

  /**
   * Deliver a batch as ONE callback invocation. The workspace listing arrives as
   * hundreds of entries at once; feeding them through `handleWatchEvent` one at
   * a time would re-render the file tree once per file.
   */
  handleWatchEvents(events: FileWatchEvent[]): void {
    if (events.length === 0) {
      return;
    }

    for (const cb of this._watchCallbacks) {
      cb(events);
    }
  }
}
